/**
 * Reading the four provenance states the API now reports.
 *
 * Pure functions only, and deliberately no *decisions*: which state a parameter
 * is in is settled in `api/services/resolution.py`, which is the only place that
 * sees readings, doses and events together. A browser-side re-derivation would
 * be a second answer to "is this chlorine reading still true", and the two would
 * drift the first time a resolution constant moved. Everything here reshapes
 * what the server already said.
 *
 * Measurements stay strings all the way to the screen. The server sends "10.0"
 * rather than 10 because a FAS-DPD titration read to one decimal says something
 * a bare integer does not, and `Number("10.0")` throws that away.
 */

import { PARAMS, type ParamKey } from "@/lib/params";
import type {
  ParamEvaluation,
  ParameterState,
  SupersedingAction,
  ValueState,
} from "@/types";

/**
 * The order the panel is read in, so a re-test list matches the rows below it.
 *
 * Mirrors `RESOLVED_PARAMETERS`. The API sends its map in this order already;
 * saying it again here means a JSON round trip that reordered the keys cannot
 * shuffle the screen.
 */
const PANEL_ORDER: string[] = [
  "fc",
  "cc",
  "ph",
  "ta",
  "ch",
  "cya",
  "tds",
  "salt",
  "water_temp_f",
];

/** Absent state means the response predates resolution — treat it as measured. */
export function stateOf(evaluation?: ParamEvaluation): ValueState {
  return evaluation?.state ?? "measured";
}

/** API Decimals arrive as strings to avoid float drift; parse at the edge. */
function numeric(raw: string | null | undefined): number | null {
  if (raw === null || raw === undefined || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * The figure that may be judged against a target — null once superseded.
 *
 * The server nulls `value` the moment a logged action moves a parameter, which
 * is what stops the comparator bar rendering a verdict on water that has since
 * had a gallon of chlorine poured into it.
 */
export function judgedValue(evaluation?: ParamEvaluation): number | null {
  return numeric(evaluation?.value);
}

/**
 * What the kit actually read, whether or not it still stands.
 *
 * Falls back to `value` so a response without the new fields renders exactly as
 * it used to.
 */
export function testedValue(evaluation?: ParamEvaluation): number | null {
  return numeric(evaluation?.last_measured_value ?? evaluation?.value);
}

export function parameterLabel(parameter: string): string {
  return PARAMS[parameter as ParamKey]?.full ?? parameter;
}

export interface RetestRow {
  parameter: string;
  label: string;
  /** The last measured figure, at the precision it was stored with. */
  was: string | null;
  /** Where the logged actions should have taken it. Null when unpredicted. */
  projected: string | null;
}

export interface RetestPlan {
  /** What moved the water, deduplicated and oldest first. */
  causes: SupersedingAction[];
  rows: RetestRow[];
  /**
   * The latest moment any cause needs before a re-test means something, or null
   * when nothing published a figure.
   *
   * The *latest*, because a re-test covers all these parameters at once: a
   * chlorine dose thirty minutes ago and stabiliser last Tuesday are ready at
   * very different times, and going out at the earlier one wastes the trip for
   * the other. One number, and it is the conservative one.
   *
   * Null is not "go now" — it is "nobody has published a wait for this", which
   * the card says by staying quiet rather than inventing a time.
   */
  readyAt: string | null;
  /**
   * The server's own sentences for these rows. Handed back so the caller can
   * drop them from "Can't say yet" — the same fact stated twice on one screen
   * reads as two problems.
   */
  notes: string[];
}

/**
 * The re-test this panel needs before any dose can be sized, or null.
 *
 * Built from `superseded` alone, not from every non-measured state. A carried
 * CYA is a real figure that still judges its target — ADR 0010 withholds only
 * the *quantity*, and sending someone to re-test on the strength of it would
 * make the app nag on the majority of readings, which is exactly the behaviour
 * that ADR was written to end. A carry the age cap expired lands in `missing`
 * and keeps the "can't say yet" treatment for the same reason: nothing was
 * logged, so there is nothing to point at.
 */
export function retestPlan(
  parameters?: Record<string, ParameterState>,
): RetestPlan | null {
  if (!parameters) return null;

  const superseded = PANEL_ORDER.map((name) => parameters[name]).filter(
    (item): item is ParameterState =>
      item !== undefined && item.state === "superseded",
  );
  if (superseded.length === 0) return null;

  // One trip to the pool with two jugs supersedes several parameters and would
  // otherwise be reported as several separate reasons to go and test.
  const causes = new Map<string, SupersedingAction>();
  for (const item of superseded) {
    for (const action of item.superseded_by ?? []) {
      const seen = causes.get(action.label);
      if (!seen || action.occurred_at < seen.occurred_at)
        causes.set(action.label, action);
    }
  }

  const settles = [...causes.values()]
    .map((action) => action.settled_at)
    .filter((at): at is string => Boolean(at));

  return {
    readyAt: settles.length ? settles.reduce((a, b) => (a > b ? a : b)) : null,
    causes: [...causes.values()].sort((a, b) =>
      a.occurred_at.localeCompare(b.occurred_at),
    ),
    rows: superseded.map((item) => ({
      parameter: item.parameter,
      label: parameterLabel(item.parameter),
      was: item.value,
      projected: item.projected_value,
    })),
    notes: superseded
      .map((item) => item.note)
      .filter((note): note is string => Boolean(note)),
  };
}

/**
 * The blocked reasons a re-test card has not already accounted for.
 *
 * `_withheld_reason` quotes the resolver's sentence verbatim when a parameter
 * blocked its own dose, and embeds it when one parameter blocked another's —
 * so containment catches both without the two sides having to agree on a
 * format. A change in the server's phrasing degrades to a sentence shown twice,
 * never to a withheld dose shown nowhere.
 */
export function remainingBlocked(
  blocked: string[],
  accountedFor: string[],
): string[] {
  return blocked.filter(
    (reason) => !accountedFor.some((note) => reason.includes(note)),
  );
}
